import { IFincaSelectedService } from "../../abstractions/services/IFincaSelectedService";
import { ClienteFincaBaseResponse } from "../../modulos/terceros/response/ClienteFincaBaseResponse";

/** Contexto in-memory de la finca seleccionada. Singleton: lo setea la sección
 *  FINCAS del aside (ruta `/finca/{id}`) y lo leen los módulos cuya ruta no
 *  lleva id. No se persiste: la navegación a `/finca/{id}` lo restablece. */
class FincaSelectedService implements IFincaSelectedService {
  /** Finca activa (o null si no hay ninguna seleccionada). */
  private _finca: ClienteFincaBaseResponse | null = null;
  /** Suscriptores notificados ante cada cambio de finca. */
  private readonly _listeners = new Set<
    (finca: ClienteFincaBaseResponse | null) => void
  >();

  /** Establece la finca activa y notifica a los suscriptores. */
  set(finca: ClienteFincaBaseResponse): void {
    this._finca = finca;
    this._notify();
  }

  /** Retorna la finca activa (o null). */
  get(): ClienteFincaBaseResponse | null {
    return this._finca;
  }

  /** Retorna la finca activa; lanza si no hay ninguna seleccionada. */
  getRequired(): ClienteFincaBaseResponse {
    if (!this._finca) {
      throw new Error("No hay finca seleccionada.");
    }
    return this._finca;
  }

  /** Limpia la finca activa y notifica a los suscriptores. */
  clear(): void {
    this._finca = null;
    this._notify();
  }

  /** Suscribe un listener a los cambios de finca; retorna la función para desuscribir. */
  subscribe(
    listener: (finca: ClienteFincaBaseResponse | null) => void,
  ): () => void {
    this._listeners.add(listener);
    return () => this._listeners.delete(listener);
  }

  /** Notifica a todos los suscriptores con la finca actual. */
  private _notify(): void {
    for (const listener of this._listeners) {
      listener(this._finca);
    }
  }
}

/** Singleton del contexto de finca seleccionada. */
export const fincaSelectedService = new FincaSelectedService();
